<script lang="ts">
	import Checkbox from '@astlai/common/Checkbox.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import type { SecondarySourceOption } from '#src/types/secondary-sources.ts';
	import { SECONDARY_SOURCE_ICONS } from '#lib/util/secondary-source-icons.ts';
	import type { I18nKey } from '#lib/i18n/translations.ts';

	interface Props {
		sources: SecondarySourceOption[];
	}
	let { sources }: Props = $props();

	let enrichedSources = $derived(
		sources.map((source) => ({
			...source,
			checked: globalState.userSettings[source.settingsKey] ?? false
		}))
	);
</script>

<div class="secondary-sources-menu">
	{#each enrichedSources as source (source.flagKey)}
		{@const SourceIcon = SECONDARY_SOURCE_ICONS[source.flagKey]}
		<div class="source-row" class:checked={source.checked}>
			<Checkbox
				checkPosition="end"
				bind:checked={
					() => source.checked, (v) => globalState.setUserSetting(source.settingsKey, v)
				}
			>
				<div class="source-icon">
					<SourceIcon />
				</div>
				<div class="text">
					<span class="name">{t(`chat.sources.${source.flagKey}` as I18nKey)}</span>
					<span class="description"
						>{t(`chat.sources.${source.flagKey}.description` as I18nKey)}</span
					>
				</div>
			</Checkbox>
		</div>
	{/each}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.secondary-sources-menu {
		@include lib.flex-column();
		gap: 0.5rem;
		width: 100%;
		padding: 0.25rem;

		.source-row {
			--checkbox-color: var(--text-tertiary);
			// The Checkbox's <label> is the clickable element, so the
			// row padding has to live on it, not on this wrapper.
			--checkbox-padding: 0.5rem 0.75rem;
			border-radius: 0.5rem;

			&:hover {
				background: var(--state-hover);
			}

			.source-icon {
				@include lib.flex-row();
				@include lib.flex-align();
				flex-shrink: 0;
				font-size: 1rem;
				color: var(--text-tertiary);
			}

			.text {
				@include lib.flex-column();
				gap: 0.125rem;
				min-width: 0;

				// Undo the global `label span` ellipsis rule so the
				// description wraps instead of overflowing the menu.
				.name,
				.description {
					white-space: normal;
					overflow-wrap: break-word;
					color: var(--text-tertiary);
				}

				.description {
					font-size: 0.75rem;
				}
			}

			&.checked {
				--checkbox-color: var(--text-primary);

				.source-icon {
					color: var(--text-accent);
				}

				.text {
					.name {
						color: var(--text-primary);
					}

					.description {
						color: var(--text-secondary);
					}
				}
			}
		}
	}
</style>
